import { useEffect, useState, useCallback } from 'react';
import {
  parseGoogleAddress,
  type ParsedAddress,
} from '@/utils/classroom/address-map-utils';
import { Button, Typography, CustomGoogleMap, Address } from '@ecdlink/ui';

const COMPONENT_HEIGHT = 150;
interface AddressMapProps {
  onClose: () => void;
  onSubmit: (addressData: ParsedAddress) => void;
}

export const AddressMap: React.FC<AddressMapProps> = ({
  onClose,
  onSubmit,
}) => {
  const [addressComponents, setAddressComponents] = useState<
    Address[] | undefined
  >();
  const [position, setPosition] = useState<{ lat: number; lng: number } | null>(
    null
  );
  const [parsedAddress, setParsedAddress] = useState<ParsedAddress | null>(
    null
  );

  const handleMapChange = useCallback(
    (data: {
      addressComponents?: Address[];
      latitude: number;
      longitude: number;
    }) => {
      setAddressComponents(data.addressComponents);
      setPosition({ lat: data.latitude, lng: data.longitude });
    },
    []
  );

  useEffect(() => {
    if (addressComponents && position) {
      const data = parseGoogleAddress(
        addressComponents,
        position.lat,
        position.lng
      );
      setParsedAddress(data);
    } else if (addressComponents) {
      // Fallback when position not yet set
      const data = parseGoogleAddress(addressComponents, -33.9249, 18.4241);
      setParsedAddress(data);
    }
  }, [addressComponents, position]);

  const saveAddress = () => {
    if (parsedAddress) {
      onSubmit(parsedAddress);
    } else if (addressComponents) {
      const fallback = parseGoogleAddress(addressComponents, -33.9249, 18.4241);
      onSubmit(fallback);
    }
    onClose();
  };

  return (
    <div>
      <CustomGoogleMap
        height={window.screen.height - COMPONENT_HEIGHT}
        onChange={handleMapChange}
      />

      <div className="min-h-64 absolute bottom-0 w-full flex-1 rounded-t-2xl bg-white px-5">
        <Typography
          type="h2"
          color="textDark"
          text="Is this address/location correct?"
          className="pt-6"
        />
        <Typography
          type="h4"
          color="textMid"
          text="Move the pin to change address"
          className="w-11/12 pt-2"
        />

        <Typography
          type="h4"
          color="secondary"
          text={parsedAddress?.formattedAddress || 'Loading address...'}
          className="my-5"
        />

        <div className="mb-5 flex flex-col gap-3">
          <Button
            type="filled"
            color="primary"
            className="max-h-10 w-full"
            icon="SaveIcon"
            onClick={saveAddress}
            disabled={!parsedAddress}
          >
            Save
          </Button>
          <Button
            type="outlined"
            color="primary"
            className="max-h-10 w-full"
            icon="XCircleIcon"
            onClick={onClose}
          >
            Cancel
          </Button>
        </div>
      </div>
    </div>
  );
};
